/**
 * Permission vocabulary of the Skills module.
 *
 * The feature declares its own permissions here instead of importing the global
 * `PERMISSIONS` catalog, so every access rule of Skills is readable — and
 * changeable — in one place inside the feature.
 *
 * Why the values still read `agent.process_flows.*`: `can()` (see
 * `features/auth/context/AuthContext.tsx`) is an exact-string lookup against the
 * set the backend sends at login. The rename Process Flows → Skills (PRD -
 * Skills) has not reached the backend permission seed
 * (`scripts/migrations/permissions-catalog.json`), so those are the only strings
 * the API actually grants; inventing `agent.skills.*` here would deny the whole
 * section to everyone but a superadmin. The verbs already match what Skills
 * needs one-to-one — including `view_executions` for "View logs".
 *
 * TODO(VOX-182): when the seed renames them, this object is the only edit.
 */
export const SKILLS_PERMISSIONS = {
  /** See the section and the list. */
  view: 'agent.process_flows.view',
  /** Create a new skill. */
  create: 'agent.process_flows.create',
  /** Open a skill in the editor and save it. */
  edit: 'agent.process_flows.edit',
  /** Duplicate a skill into a draft (VOX-205/206). */
  duplicate: 'agent.process_flows.duplicate',
  /** Delete a skill. */
  delete: 'agent.process_flows.delete',
  /** Flip a skill between Draft and Active. */
  toggleActive: 'agent.process_flows.toggle_active',
  /** Read the skill's execution log ("View logs"). */
  viewLogs: 'agent.process_flows.view_executions',
  /**
   * Connect and disconnect Silia tables from the Tables panel (VOX-191).
   *
   * The odd one of the list: it is not a Skills verb but the agent's own, because
   * the relation being granted is the agent's and not this skill's — the same
   * string `AgentSettingsPage` checks before opening the very same modal. Listed
   * here anyway so the panel reads its rule from this file like every other
   * access rule of the module, instead of reaching for the global catalog.
   */
  connectTables: 'agent.connect_tables',
} as const;

/** Any permission string of this module. */
export type SkillsPermission = (typeof SKILLS_PERMISSIONS)[keyof typeof SKILLS_PERMISSIONS];
